<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcMeterInvoices',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    meter: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_6804155476809633_editable = computed(() => false);
  const b_6804155476809633_columns = computed(() => [
    {
      name: 'invoice_number',
      type: 'template',
      header: $fn.value.pgettext('MeterInvoices', 'Invoice'),
      width: '130px',
      value:
        '<div>\n\
  <ItpLink :to="{name: \'app.invoice\', params: { id: row.invoice_id }}" >\n\
    {{ row.invoice_number }}\n\
  </ItpLink>\n\
  <ItpText muted>{{date(row.invoice_date) }}</ItpText>\n\
  <ItpLink :to="{name: \'app.company\', params: { id: row.client_id }}" >\n\
    {{ row.client_name }}\n\
  </ItpLink>\n\
</div>\n\
',
    },
    {
      name: 'contract_number',
      type: 'template',
      header: $fn.value.pgettext('MeterInvoices', 'Contract / Rental'),
      width: '130px',
      value:
        '<div>\n\
  <ItpLink :to="{name: \'app.rental\', params: { id: row.contract_id }}" >\n\
    {{ row.contract_number }}\n\
  </ItpLink>\n\
  <ItpLink :to="{name: \'app.rental\', params: { id: row.rental_id }}" >\n\
    {{ row.rental_name }}\n\
  </ItpLink>\n\
</div>\n\
',
    },
    {
      name: 'from_date',
      header: $fn.value.pgettext('MeterInvoices', 'From'),
      type: 'template',
      value:
        '<div>\n\
  {{number(row.from_amount) }} {{ row.from_unit }}<br />\n\
  <ItpText muted>{{date(row.from_date) }}</ItpText>\n\
</div>\n\
',
      width: '150px',
    },
    {
      name: 'to_date',
      header: $fn.value.pgettext('MeterInvoices', 'To'),
      type: 'template',
      value:
        '<div>\n\
  {{number(row.to_amount) }} {{ row.to_unit }}<br />\n\
  <ItpText muted>{{date(row.to_date) }}</ItpText>\n\
</div>\n\
',
      width: '150px',
    },
    {
      name: 'real_consumption_amount',
      header: $fn.value.pgettext('MeterInvoices', 'Consumption'),
      type: 'template',
      value:
        '<div>\n\
  {{number(row.real_consumption_amount) }} {{ row.real_consumption_unit }}\n\
</div>\n\
',
      width: '110px',
    },
    {
      name: 'billing_multiplier',
      header: $fn.value.pgettext('MeterInvoices', 'Mp.'),
      tooltip: $fn.value.pgettext('MeterInvoices', 'Multiplier'),
      type: 'number',
      width: '70px',
    },
    {
      name: 'quantity',
      header: $fn.value.pgettext('MeterInvoices', 'Quantity'),
      type: 'template',
      value: '<span>{{number(row.quantity) }} {{ row.unit }}</span>',
      width: '100px',
    },
    {
      name: 'unit_price',
      header: $fn.value.pgettext('MeterInvoices', 'Price'),
      type: 'number',
      width: '70px',
    },
    {
      name: 'net_amount',
      header: $fn.value.pgettext('MeterInvoices', 'Net'),
      type: 'number',
      width: '80px',
    },
    {
      name: 'amount',
      header: $fn.value.pgettext('MeterInvoices', 'Amount'),
      type: 'number',
      width: '80px',
    },
    {
      name: 'currency',
      header: $fn.value.pgettext('MeterInvoices', 'Currency'),
      type: 'number',
      width: '80px',
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
    b_6804155476809633_editable: b_6804155476809633_editable.value,
    b_6804155476809633_columns: b_6804155476809633_columns.value,
  }));

  const $$load_items = async () => {
    return $fn.value.fetch('list_meter_invoices', {
      parameters: { meter_id: props.meter.id },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_6804155476809633_editable', nodeId: '6804155476809633' },
      { name: 'b_6804155476809633_columns', nodeId: '6804155476809633' },
    ],
    loaders: { items: $$load_items },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      items,
      ux,
      b_6804155476809633_editable,
      b_6804155476809633_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div v-if="!items || !items.length">
    <ItpAlert
      show
      :message="pgettext('Meter', 'This meter has no related invoices.')"
    >
    </ItpAlert>
  </div>
  <ItpDataTable
    tableId="MeterInvoices"
    :editable="b_6804155476809633_editable"
    paging
    :value="
      _.orderBy(
        items,
        ['invoice_date', 'reading_time', 'created_at'],
        ['desc', 'desc', 'desc']
      )
    "
    :columns="b_6804155476809633_columns"
    v-if="items && items.length"
  >
  </ItpDataTable>
</template>
